import { useEffect, useMemo, useRef } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import StandardDialog from "../../components/StandardDialog.js";
import Collection from "../curate/CollectionWithVirtualisation.js";
import CollectionSkeleton from "../curate/CollectionSkeleton.js";
import NotFoundPage from "../../routes/NotFoundPage.js";
import useApi from "../../hooks/useApi.js";
import { useAuth } from "../../context/authContext.js";
import { useThemeContext } from "../../context/themeContext.js";
import { useDetailNavigation } from "../../hooks/useDetailNavigation.js";
import { useDocumentMeta } from "../../hooks/useDocumentMeta.js";
import { buildListPath, slugify } from "../../utils/detailPaths.js";
import { isStandoutsList, isUserOriginList } from "../../utils/listSchema.js";
import { getContentLabel } from "../../utils/utils.js";

// Mirrors the wording of the backend's unfurl endpoint (GET /meta/list/<id> in
// flask-server/app/services/meta.py) so the tab title a signed-in user sees and
// the description a crawler unfurls agree with each other. MIXED and unknown
// types fall back to "item(s)".
const CONTENT_TYPE_NOUNS = {
  movie: ["movie", "movies"],
  tvshow: ["TV show", "TV shows"],
  book: ["book", "books"],
};

function nounForContentType(contentType, count) {
  const [singular, plural] = CONTENT_TYPE_NOUNS[contentType] ?? [
    "item",
    "items",
  ];
  return count === 1 ? singular : plural;
}

// The collection dialog's paper is capped at 1000px and, on mobile, sized to the
// viewport minus StandardDialog's m:2 inset (32px) and the safe-area top/bottom insets
// StandardDialog now pads its container by (0px outside the installed-PWA/notched case,
// where 100dvh otherwise includes them and the paper would overflow under the status
// bar/home indicator) — repeated here once instead of once per call site.
function useDialogSx(fullscreen) {
  return {
    "& .MuiDialog-paper": {
      maxWidth: "1000px",
      backgroundImage: "none",
      ...(fullscreen && {
        height: "calc(100dvh - var(--safe-top) - var(--safe-bottom) - 32px)",
        maxHeight: "calc(100dvh - var(--safe-top) - var(--safe-bottom) - 32px)",
      }),
    },
  };
}

// TV standouts are seasons, so their "finished" count is too
const STANDOUT_NOUNS = {
  movie: ["movie", "movies"],
  tvshow: ["season", "seasons"],
  book: ["book", "books"],
};

// The dialog's description for a standouts list (stored without one), e.g.
// "You've picked 4 standouts from the 33 movies you've finished so far in 2026."
// A past year reads "...you finished in 2025." Counts the items actually
// shown, so it follows the ?type= filter.
function describeStandouts({ list, items, typeFilter, isOwner }) {
  const { year, finished_counts: finishedCounts } = list.standouts;
  const finished = typeFilter
    ? (finishedCounts[typeFilter] ?? 0)
    : Object.values(finishedCounts).reduce((sum, count) => sum + count, 0);
  const [one, many] = typeFilter ? STANDOUT_NOUNS[typeFilter] : ["thing", "things"];
  // The viewer's local year, matching how standouts years are chosen. A year
  // still in progress reads in the present perfect throughout; a past one in
  // the simple past.
  const isCurrentYear = year === new Date().getFullYear();
  const subject = isOwner ? "You" : `@${list.username}`;
  const picked = isCurrentYear
    ? `${subject}${isOwner ? "'ve" : " has"} picked`
    : `${subject} picked`;
  const finishedPhrase = isCurrentYear
    ? `${isOwner ? "you've" : "they've"} finished so far in ${year}`
    : `${isOwner ? "you" : "they"} finished in ${year}`;
  return (
    `${picked} ${items.length} ${items.length === 1 ? "standout" : "standouts"} ` +
    `from the ${finished} ${finished === 1 ? one : many} ${finishedPhrase}.`
  );
}

/**
 * Single mount point for the collection dialog. listId and slug come from the
 * URL, which is the only place this identity is held.
 *
 * ?type=movie|tvshow|book shows a mixed list filtered to one content type (e.g.
 * a profile's standouts opened from that type's tab). Applied only when the
 * dialog is read-only: editing/reordering assumes the full item list.
 */
export default function CollectionDetailRoute() {
  const { listId: listIdParam, slug } = useParams();
  const [searchParams] = useSearchParams();
  const typeFilter = searchParams.get("type");
  const navigate = useNavigate();
  const location = useLocation();
  const { fetchList } = useApi();
  const { user } = useAuth();
  const { isTouchDevice } = useThemeContext();
  const theme = useTheme();
  const fullscreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { closeDetail } = useDetailNavigation();
  const dialogSx = useDialogSx(fullscreen);

  // Holds a deferred cache invalidation function registered by child
  // mutations, flushed on close so a mutation that removes the viewed
  // collection from a list elsewhere doesn't cause a render crash while the
  // dialog is still open.
  const pendingInvalidation = useRef(null);

  const listId = Number(listIdParam);
  const isValidId = Number.isInteger(listId) && listId > 0;

  // Curate's openList seeds this key with its already-fetched full list, so
  // opening from there paints instantly with no fetch. Explore/CuratePreview
  // hold only capped payloads and can't seed it, so opening from there always
  // fetches. throwOnError: false — a private or deleted list 403s/404s here,
  // which should render the unavailable state below rather than the global
  // error boundary.
  const { data: list, isError } = useQuery({
    queryKey: ["list", listId],
    queryFn: () => fetchList(listId),
    enabled: isValidId,
    throwOnError: false,
  });

  useEffect(() => {
    if (!list?.title) return;
    const canonicalSlug = slugify(list.title);
    if (slug !== canonicalSlug) {
      // Keep the query string — it carries the ?type= filter
      navigate(`${buildListPath(list)}${location.search}`, {
        replace: true,
        state: location.state,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list?.title, listId, slug]);

  // Read-only unless it's literally your own list, or (for canonical lists
  // only) you're an admin on desktop — derived from the list itself rather
  // than from which page it was opened from, so the answer no longer depends
  // on that. Standouts are always read-only here, owner and admin included:
  // they're edited through /standouts, never the generic list routes.
  const readOnly =
    !list ||
    isStandoutsList(list) ||
    list.username !== user?.username ||
    (!isUserOriginList(list) && !(user?.isAdmin && !isTouchDevice));

  // Narrow to one content type when asked (read-only only — see above). The
  // title says so, and sharing keeps the filter in the link.
  const activeTypeFilter =
    list && readOnly && CONTENT_TYPE_NOUNS[typeFilter] ? typeFilter : null;
  const isOwner = !!list && list.username === user?.username;

  // Memoised: Collection copies its list prop into state whenever it changes
  const displayList = useMemo(() => {
    if (!list) return list;
    let shown = list;
    if (activeTypeFilter) {
      shown = {
        ...shown,
        title: `${list.title} · ${getContentLabel({ type: activeTypeFilter, plural: true })}`,
        items: list.items.filter((item) => item.content_type === activeTypeFilter),
        share_path: `${buildListPath(list)}?type=${activeTypeFilter}`,
      };
    }
    // Standouts are stored without a description; the dialog writes one
    if (isStandoutsList(list) && list.standouts) {
      shown = {
        ...shown,
        description: describeStandouts({
          list,
          items: shown.items,
          typeFilter: activeTypeFilter,
          isOwner,
        }),
      };
    }
    return shown;
  }, [list, activeTypeFilter, isOwner]);
  const nounType = activeTypeFilter ?? list?.content_type;

  const itemCount = displayList?.items?.length ?? 0;
  useDocumentMeta({
    title: displayList
      ? displayList.origin === "canonical"
        ? `${displayList.title}, a collection of ${CONTENT_TYPE_NOUNS[displayList.content_type]?.[1] ?? "items"} by Curate`
        : `${displayList.title}, ${itemCount} ${nounForContentType(nounType, itemCount)} curated by @${displayList.username} on Curate`
      : undefined,
  });

  const handleClose = () => {
    pendingInvalidation.current?.();
    pendingInvalidation.current = null;
    closeDetail();
  };

  if (!isValidId) return <NotFoundPage />;

  if (isError) {
    return (
      <StandardDialog
        open
        isFullscreen={fullscreen}
        onClose={closeDetail}
        sx={dialogSx}
      >
        <Box
          sx={{
            position: "relative",
            height: "100%",
            minHeight: 240,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            p: 4,
          }}
        >
          <Tooltip title="Close">
            <IconButton
              onClick={closeDetail}
              sx={{ position: "absolute", top: 8, right: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="body1">
            This collection is private or no longer available
          </Typography>
        </Box>
      </StandardDialog>
    );
  }

  return (
    <StandardDialog
      open
      isFullscreen={fullscreen}
      onClose={handleClose}
      sx={dialogSx}
    >
      {!list ? (
        <CollectionSkeleton fullScreen={fullscreen} />
      ) : (
        <Collection
          list={displayList}
          onDelete={closeDetail}
          onClose={handleClose}
          onPendingInvalidation={(fn) => {
            pendingInvalidation.current = fn;
          }}
          fullScreen={fullscreen}
          readOnly={readOnly}
          // Previously only shown from the Explore/canonical-browsing dialogs
          // (never Curate's own-collections one) — origin is the closest
          // list-derived proxy for that distinction now that this is one
          // component regardless of which page opened it.
          enableTrackingHelper={list.origin === "canonical"}
          // Standouts mix content types; a type filter makes it redundant
          showType={isStandoutsList(list) && !activeTypeFilter}
        />
      )}
    </StandardDialog>
  );
}
